/**
 * Which Meta pixel a storefront's browser-side events belong to.
 *
 * One deployment serves every storefront — the country is a property of the
 * REQUEST, not of the build (same reason DOMAIN_MAP_TO_CURRENCY exists). The
 * pixel id was nonetheless a single hardcoded constant in app/layout.tsx, so
 * every fbq() PageView from every country was attributed to the Slovenian
 * pixel: the Croatian, Slovak and Polish pixels never saw a browser event at
 * all, and the Slovenian one was diluted with traffic from four countries.
 *
 * The server-side Conversions API has always picked its pixel per country
 * (getPixelIdFromCountry in photo-upload-backend). This is the browser half of
 * that same mapping, and the two must agree: events for one customer split
 * across two pixels cannot be matched or deduplicated by Meta.
 *
 * Ids are read from build-time env so they live with the other deployment
 * config rather than in the repo. NEXT_PUBLIC_* is inlined by Next at build
 * time, which is why every key is written out as a literal `process.env.X`
 * lookup — a computed key would silently resolve to undefined.
 */

/** Fallback when a country has no pixel configured: the Slovenian pixel. */
export const DEFAULT_META_PIXEL_ID =
  process.env.NEXT_PUBLIC_META_PIXEL_ID_SI || "2029220584069190";

/**
 * Top-level domain → pixel id. Keys mirror DOMAIN_MAP_TO_CURRENCY and the
 * backend's country branches, so a storefront cannot be live for currency but
 * invisible to tracking.
 */
export const META_PIXEL_ID_BY_TLD: Record<string, string | undefined> = {
  si: process.env.NEXT_PUBLIC_META_PIXEL_ID_SI,
  hr: process.env.NEXT_PUBLIC_META_PIXEL_ID_HR,
  sk: process.env.NEXT_PUBLIC_META_PIXEL_ID_SK,
  pl: process.env.NEXT_PUBLIC_META_PIXEL_ID_PL,
  at: process.env.NEXT_PUBLIC_META_PIXEL_ID_AT,
  cz: process.env.NEXT_PUBLIC_META_PIXEL_ID_CZ,
  de: process.env.NEXT_PUBLIC_META_PIXEL_ID_DE,
  // Romania prices in RON but has no branch in the backend's
  // getPixelIdFromCountry either, so its server events fall through to the
  // Slovenian pixel today. The slot is here so the two halves can be pointed at
  // a real RO pixel together; until one is configured it behaves as it does now.
  ro: process.env.NEXT_PUBLIC_META_PIXEL_ID_RO,
};

/**
 * The TLD of a hostname, lowercased. Tolerates a port (the Host header carries
 * one in development) and returns "" for anything without a dot, so localhost
 * and bare IPs fall through to the default rather than matching a country.
 */
export function tldFromHostname(hostname: string | null | undefined): string {
  if (!hostname) return "";
  const withoutPort = hostname.split(":")[0].trim().toLowerCase();
  const parts = withoutPort.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
}

/**
 * The pixel this request's storefront reports to.
 *
 * Falls back to the Slovenian pixel for an unmapped or unconfigured country:
 * that is the behaviour this module replaces, so an unset variable can only
 * ever leave a country no worse off than it was — never dark.
 */
export function resolveMetaPixelId(
  hostname: string | null | undefined,
): string {
  return META_PIXEL_ID_BY_TLD[tldFromHostname(hostname)] || DEFAULT_META_PIXEL_ID;
}
